import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  });
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [currentBranchId, setCurrentBranchIdState] = useState(() => localStorage.getItem("currentBranchId") || null);

  const setCurrentBranchId = useCallback((id) => {
    if (id) localStorage.setItem("currentBranchId", id);
    else localStorage.removeItem("currentBranchId");
    setCurrentBranchIdState(id);
  }, []);

  const refreshBranches = useCallback(async () => {
    try {
      const { data } = await api.get("/branches");
      setBranches(data);
      // If no current branch picked, default to user's branch or first available
      if (!localStorage.getItem("currentBranchId")) {
        const u = JSON.parse(localStorage.getItem("user") || "{}");
        const fallback = u?.branch_id || data[0]?.id;
        if (fallback) setCurrentBranchId(fallback);
      }
    } catch (e) {
      console.error("[auth] Failed to fetch branches:", e);
    }
  }, [setCurrentBranchId]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token && !user) {
      setLoading(true);
      api.get("/auth/me")
        .then(r => {
          setUser(r.data);
          localStorage.setItem("user", JSON.stringify(r.data));
          if (!localStorage.getItem("currentBranchId") && r.data.branch_id) {
            setCurrentBranchId(r.data.branch_id);
          }
        })
        .catch(() => { localStorage.removeItem("token"); localStorage.removeItem("user"); })
        .finally(() => setLoading(false));
    }
    if (token) refreshBranches();
  }, []); // eslint-disable-line

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("token", data.token);
    localStorage.setItem("user", JSON.stringify(data.user));
    setUser(data.user);
    if (data.user.branch_id) setCurrentBranchId(data.user.branch_id);
    await refreshBranches();
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("currentBranchId");
    setUser(null); setBranches([]); setCurrentBranchIdState(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, branches, currentBranchId, setCurrentBranchId, refreshBranches }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export const ROLES = { ADMIN: "admin", MANAGER: "manager", BILLING: "billing", GROOMING: "grooming", INVENTORY: "inventory" };

export const ROLE_LABEL = {
  admin: "Admin", manager: "Manager", billing: "Billing Staff", grooming: "Grooming Staff", inventory: "Inventory Staff",
};
